// First-party VOD upload controller: Uppy multipart wired to the CMS API's
// session endpoints (open / part URLs / complete / poll / abort), with
// verified resume after reload and self-healing finalize retries.

import { useCallback, useEffect, useRef, useState } from 'react'
import Uppy from '@uppy/core'
import type { Meta, UppyFile } from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import { ConflictError, PreconditionFailedError } from '../services/api'
import {
  abortVodUpload,
  completeVodUpload,
  fetchVodUploadPartUrls,
  openVodUpload,
  pollVodUpload,
} from '../services/vodUpload'
import { verifyUploadedParts } from '../utils/uploadVerification'
import type {
  VodUploadCompleteResult,
  VodUploadSession,
  VodUploadedPart,
} from '../types/vod'

const POLL_INTERVAL_MS = 5_000
const STALE_VALIDATING_MS = 5 * 60_000
const PART_URL_BATCH = 10
// Presigned part URLs live ~1h server-side; refresh well before that
const PART_URL_TTL_MS = 45 * 60_000
const UPLOAD_CONCURRENCY = 5

export type VodUploadPhase =
  | 'loading'
  | 'idle'
  | 'resume-prompt'
  | 'active-elsewhere'
  | 'verifying'
  | 'mismatch'
  | 'uploading'
  | 'validating'
  | 'completed'
  | 'failed'

export interface VodUploadUiState {
  phase: VodUploadPhase
  /** Latest server session view (poll / complete responses) */
  session: VodUploadSession | null
  bytesUploaded: number
  bytesTotal: number
  isPaused: boolean
  warnings: string[]
  /** Inline, non-terminal problem (open failure, part retry exhaustion, …) */
  errorMessage: string | null
  verifyProgress: { verified: number; total: number } | null
  /** A selection is being processed — the picker stays disabled until it settles */
  isOpening: boolean
}

const INITIAL_STATE: VodUploadUiState = {
  phase: 'loading',
  session: null,
  bytesUploaded: 0,
  bytesTotal: 0,
  isPaused: false,
  warnings: [],
  errorMessage: null,
  verifyProgress: null,
  isOpening: false,
}

export const VOD_UPLOAD_FAILURE_MESSAGES: Record<string, string> = {
  NOT_MP4: 'This file is not a valid MP4 — re-export it and upload again.',
  UNPARSEABLE: 'This file is not a valid MP4 — re-export it and upload again.',
  NO_VIDEO_STREAM: 'The file contains no video stream — re-export it and upload again.',
  SIZE_MISMATCH:
    'The uploaded bytes do not match the declared file size — try the upload again.',
  EXPIRED: 'The upload session expired — start a new upload.',
}

interface SessionInfo {
  uploadId: string
  partSize: number
  partCount: number
  declaredSizeBytes: number
  originalFilename: string | null
}

const UPLOAD_ACTIVE_ELSEWHERE_MESSAGE =
  'An upload for this asset is already active in another tab — finish or cancel it there first.'

const storageKey = (assetId: string) => `tc-vod-upload:${assetId}`

const sessionInfoFromView = (view: VodUploadSession): SessionInfo => ({
  uploadId: view.upload_id,
  partSize: view.part_size,
  partCount: view.part_count,
  declaredSizeBytes: view.declared_size_bytes,
  originalFilename: view.original_filename,
})

const isStaleValidating = (view: VodUploadSession): boolean => {
  if (!view.validating_started_at) return true
  return Date.now() - new Date(view.validating_started_at).getTime() > STALE_VALIDATING_MS
}

type SetUiState = (patch: Partial<VodUploadUiState>) => void

class VodUploadController {
  private readonly assetId: string
  private readonly emit: SetUiState
  private readonly onCompleted: () => void
  private readonly expectedMasterVersion: () => number

  private phase: VodUploadPhase = 'loading'
  private uppy: Uppy | null = null
  private uppyFileId: string | null = null
  private session: SessionInfo | null = null
  private uploadedParts: VodUploadedPart[] = []
  private pendingFile: File | null = null
  private urlCache = new Map<number, { url: string; fetchedAt: number }>()
  private urlBatchPromises = new Map<number, Promise<void>>()
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private completing = false
  private destroyed = false
  private lockRelease: (() => void) | null = null
  private selecting = false

  constructor(
    assetId: string,
    emit: SetUiState,
    onCompleted: () => void,
    expectedMasterVersion: () => number
  ) {
    this.assetId = assetId
    this.emit = emit
    this.onCompleted = onCompleted
    this.expectedMasterVersion = expectedMasterVersion
  }

  private patch(patch: Partial<VodUploadUiState>): void {
    if (patch.phase) this.phase = patch.phase
    this.emit(patch)
  }

  // ----------------------------------------------------------------
  // Lifecycle
  // ----------------------------------------------------------------

  async init(): Promise<void> {
    const uploadId = localStorage.getItem(storageKey(this.assetId))
    if (!uploadId) {
      this.patch({ phase: 'idle' })
      return
    }

    try {
      const data = await pollVodUpload(this.assetId, uploadId)
      if (this.destroyed) return
      this.session = sessionInfoFromView(data.session)
      this.patch({ session: data.session, warnings: data.session.warnings })

      switch (data.session.state) {
        case 'COMPLETED':
          this.clearStoredSession()
          this.patch({ phase: 'completed' })
          this.onCompleted()
          break
        case 'ABORTED':
          this.clearStoredSession()
          this.patch({ phase: 'idle' })
          break
        case 'FAILED':
          this.clearStoredSession()
          this.patch({ phase: 'failed' })
          break
        case 'VALIDATING':
          this.patch({ phase: 'validating' })
          this.startPolling()
          if (isStaleValidating(data.session)) void this.autoComplete()
          break
        case 'UPLOADING':
          if (data.assembled) {
            this.patch({ phase: 'validating' })
            this.startPolling()
            void this.autoComplete()
          } else {
            this.uploadedParts = data.uploadedParts ?? []
            if (this.uploadedParts.length >= data.session.part_count) {
              // Every part landed; only the finalize was interrupted. The server
              // reconstructs the manifest from S3, so this needs no file and no
              // re-hash — and rescues an editor who no longer has the file.
              this.patch({ phase: 'validating' })
              this.startPolling()
              void this.autoComplete()
            } else if (await this.isLockHeldElsewhere()) {
              if (this.destroyed) return
              this.patch({ phase: 'active-elsewhere' })
              this.startPolling()
            } else {
              this.patch({ phase: 'resume-prompt' })
            }
          }
          break
      }
    } catch (err) {
      if (this.destroyed) return
      if (/not found/i.test((err as Error).message || '')) {
        // Session unknown to the server — nothing to resume
        this.clearStoredSession()
        this.patch({ phase: 'idle' })
      } else {
        // Transient failure (network, 5xx, auth) — keep the handle; discarding
        // it would strand a live server session behind the one-in-flight 409
        this.patch({
          phase: 'idle',
          errorMessage:
            'Could not check for an interrupted upload on this asset — reload the page to retry before starting a new upload.',
        })
      }
    }
  }

  destroy(): void {
    this.destroyed = true
    this.stopPolling()
    this.releaseTabLock()
    this.uppy?.destroy()
    this.uppy = null
  }

  // ----------------------------------------------------------------
  // Cross-tab mutex — two tabs must never PUT parts into one session
  // (a same-sized different file would splice; Web Locks closes the
  // only path where the shared localStorage handle makes that possible)
  // ----------------------------------------------------------------

  private async acquireTabLock(): Promise<boolean> {
    if (this.lockRelease) return true
    if (!('locks' in navigator)) return true
    return new Promise<boolean>((resolve) => {
      navigator.locks
        .request(storageKey(this.assetId), { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolve(false)
            return
          }
          resolve(true)
          // Hold the lock until this controller explicitly releases it
          return new Promise<void>((release) => {
            this.lockRelease = release
          })
        })
        .catch(() => resolve(true)) // lock API unavailable — degrade open
    })
  }

  private releaseTabLock(): void {
    this.lockRelease?.()
    this.lockRelease = null
  }

  /** Non-disruptive probe: is another tab holding this asset's upload lock? */
  private async isLockHeldElsewhere(): Promise<boolean> {
    if (this.lockRelease) return false
    if (!('locks' in navigator)) return false
    try {
      const snapshot = await navigator.locks.query()
      const key = storageKey(this.assetId)
      return (snapshot.held ?? []).some((lock) => lock.name === key)
    } catch {
      return false
    }
  }

  // ----------------------------------------------------------------
  // Public actions
  // ----------------------------------------------------------------

  async selectFile(file: File, phase: VodUploadPhase): Promise<void> {
    if (this.selecting) return
    this.patch({ errorMessage: null })

    if (!/\.mp4$/i.test(file.name)) {
      this.patch({ errorMessage: 'Only .mp4 files can be uploaded.' })
      return
    }

    if (!(await this.acquireTabLock())) {
      this.patch({ errorMessage: UPLOAD_ACTIVE_ELSEWHERE_MESSAGE })
      return
    }

    this.selecting = true
    this.patch({ isOpening: true })
    try {
      if (phase === 'resume-prompt' || phase === 'mismatch') {
        await this.verifyAndResume(file)
      } else {
        await this.startFreshUpload(file)
      }
    } finally {
      this.selecting = false
      if (!this.destroyed) this.patch({ isOpening: false })
    }
  }

  pauseResume(): void {
    if (!this.uppy || !this.uppyFileId) return
    const isPaused = this.uppy.pauseResume(this.uppyFileId)
    this.patch({ isPaused: Boolean(isPaused) })
  }

  /**
   * Cancel the in-flight session: stop Uppy, abort server-side, and only
   * forget the session handle once the server confirmed it is terminal —
   * otherwise the still-open session would be stranded behind the 409 guard.
   * Returns whether the session is gone (terminal or never existed).
   */
  async cancel(): Promise<boolean> {
    if (!(await this.acquireTabLock())) {
      this.patch({ errorMessage: UPLOAD_ACTIVE_ELSEWHERE_MESSAGE })
      return false
    }
    this.stopPolling()
    this.teardownUppy()
    const uploadId = this.session?.uploadId
    if (uploadId) {
      try {
        await abortVodUpload(this.assetId, uploadId)
      } catch (err) {
        const alreadyGone =
          err instanceof ConflictError || /not found/i.test((err as Error).message || '')
        if (!alreadyGone) {
          // Abort never reached the server; keep the handle so the session
          // stays pollable/resumable instead of stranding it
          if (!this.destroyed) {
            this.patch({
              phase: 'resume-prompt',
              isPaused: false,
              errorMessage:
                'Cancel did not reach the server — the upload session is still open. Retry the cancel, or re-select the file to resume.',
            })
          }
          return false
        }
        // ConflictError: session already terminal server-side — safe to forget
      }
    }
    this.session = null
    this.uploadedParts = []
    this.clearStoredSession()
    this.releaseTabLock()
    if (!this.destroyed) {
      this.patch({
        phase: 'idle',
        session: null,
        bytesUploaded: 0,
        bytesTotal: 0,
        isPaused: false,
        warnings: [],
        verifyProgress: null,
      })
    }
    return true
  }

  /** Mismatch path: abort the stuck session, then upload the picked file fresh */
  async startOver(): Promise<void> {
    const file = this.pendingFile
    this.pendingFile = null
    if (!(await this.cancel())) return
    if (file) await this.startFreshUpload(file)
  }

  /** Back to the file picker after a terminal state (failed / completed replace) */
  reset(): void {
    this.teardownUppy()
    this.session = null
    this.uploadedParts = []
    this.clearStoredSession()
    this.releaseTabLock()
    this.patch({ ...INITIAL_STATE, phase: 'idle' })
  }

  // ----------------------------------------------------------------
  // Fresh upload
  // ----------------------------------------------------------------

  private async startFreshUpload(file: File): Promise<void> {
    if (!(await this.acquireTabLock())) {
      this.patch({ errorMessage: UPLOAD_ACTIVE_ELSEWHERE_MESSAGE })
      return
    }
    try {
      const opened = await openVodUpload(this.assetId, {
        filename: file.name,
        // Extension is already validated; browsers label MP4s inconsistently
        // (application/mp4, octet-stream) and the backend allowlist is exact
        content_type: 'video/mp4',
        size_bytes: file.size,
        expected_master_version: this.expectedMasterVersion(),
      })
      // Persist the handle before anything can bail — a session the server
      // just created must stay discoverable (resumable) even if the editor
      // navigated away while the request was in flight
      this.session = {
        uploadId: opened.upload_id,
        partSize: opened.part_size,
        partCount: opened.part_count,
        declaredSizeBytes: file.size,
        originalFilename: file.name,
      }
      localStorage.setItem(storageKey(this.assetId), opened.upload_id)
      if (this.destroyed) return

      this.uploadedParts = []
      this.urlCache.clear()
      this.urlBatchPromises.clear()
      const now = Date.now()
      for (const part of opened.parts) {
        this.urlCache.set(part.part_number, { url: part.url, fetchedAt: now })
      }

      this.startUppy(file)
    } catch (err) {
      if (this.destroyed) return
      if (err instanceof PreconditionFailedError) {
        // Stale page — refresh the asset so the current master is on screen
        this.patch({ errorMessage: err.message })
        this.onCompleted()
        this.releaseTabLock()
        return
      }
      const message =
        err instanceof ConflictError
          ? `${err.message}. If it was abandoned, it expires automatically; an upload started in this browser can be resumed by reloading this page.`
          : (err as Error).message || 'Failed to open the upload session'
      this.patch({ errorMessage: message })
      this.releaseTabLock()
    }
  }

  // ----------------------------------------------------------------
  // Verified resume
  // ----------------------------------------------------------------

  private async verifyAndResume(file: File): Promise<void> {
    const session = this.session
    if (!session) return

    if (file.size !== session.declaredSizeBytes) {
      this.pendingFile = file
      this.patch({
        phase: 'mismatch',
        errorMessage: `The selected file is ${file.size.toLocaleString()} bytes, but the interrupted upload was ${session.declaredSizeBytes.toLocaleString()} bytes.`,
      })
      return
    }

    this.patch({ phase: 'verifying', verifyProgress: { verified: 0, total: 1 } })

    try {
      // Re-poll so the landed-part list (and its ETags) is current
      const data = await pollVodUpload(this.assetId, session.uploadId)
      if (this.destroyed) return
      if (data.session.state !== 'UPLOADING' || data.assembled) {
        // The session moved on while the editor was picking — re-enter via init
        await this.init()
        return
      }
      this.uploadedParts = data.uploadedParts ?? []

      const result = await verifyUploadedParts(
        file,
        session.partSize,
        this.uploadedParts,
        (verified, total) => this.patch({ verifyProgress: { verified, total } })
      )
      if (this.destroyed) return

      if (!result.ok) {
        this.pendingFile = file
        this.patch({
          phase: 'mismatch',
          verifyProgress: null,
          errorMessage: `Part ${result.mismatchedPart} of the selected file does not match the bytes already uploaded — this looks like a different file (a re-export?).`,
        })
        return
      }

      this.patch({ verifyProgress: null })
      this.startUppy(file)
    } catch (err) {
      if (this.destroyed) return
      this.patch({
        phase: 'resume-prompt',
        verifyProgress: null,
        errorMessage: (err as Error).message || 'Failed to verify the selected file',
      })
    }
  }

  // ----------------------------------------------------------------
  // Uppy wiring — multipart hooks mapped onto our endpoints
  // ----------------------------------------------------------------

  private startUppy(file: File): void {
    this.teardownUppy()
    const session = this.session
    if (!session) return
    const { uploadId } = session
    // Opaque to S3 — Uppy only round-trips key/uploadId between our own hooks
    const key = `vod-asset/${this.assetId}`

    const uppy = new Uppy({ autoProceed: false, allowMultipleUploadBatches: false })
    uppy.use(AwsS3, {
      shouldUseMultipart: true,
      limit: UPLOAD_CONCURRENCY,
      getChunkSize: () => session.partSize,
      createMultipartUpload: async () => ({ uploadId, key }),
      // Only the parts this client hashed against the selected file. Re-polling
      // here would also return parts that landed after verification (a late PUT
      // from a dead page), and Uppy would skip them unverified.
      listParts: async () =>
        this.uploadedParts.map((p) => ({
          PartNumber: p.part_number,
          ETag: p.etag,
          Size: this.sizeOfPart(p.part_number),
        })),
      signPart: async (_file, { partNumber }) => ({
        method: 'PUT' as const,
        url: await this.getPartUrl(partNumber),
      }),
      completeMultipartUpload: async (_file, { parts }) => {
        if (this.destroyed || this.session?.uploadId !== uploadId) return {}
        this.patch({ phase: 'validating' })
        this.startPolling()
        const manifest = parts.map((p, index) => ({
          part_number: p.PartNumber ?? index + 1,
          etag: p.ETag ?? '',
        }))
        try {
          const result = await completeVodUpload(this.assetId, uploadId, manifest)
          this.handleCompleteResult(uploadId, result)
        } catch (err) {
          // 400 manifest rejection and friends — the poll loop keeps healing
          if (this.session?.uploadId === uploadId) {
            this.patch({ errorMessage: (err as Error).message })
          }
        }
        return {}
      },
      // Deliberate no-op: Uppy calls this when an upload errors out, but a
      // failed attempt must stay resumable — aborting the server session
      // (which burns the version) is only ever an explicit editor action.
      abortMultipartUpload: async () => {},
    })

    uppy.on('upload-progress', (_file, progress) => {
      this.patch({
        bytesUploaded: progress.bytesUploaded ?? 0,
        bytesTotal: progress.bytesTotal ?? file.size,
      })
    })
    uppy.on('upload-error', (_file, error) => {
      this.patch({
        errorMessage: `${error?.message || 'Upload failed'} — pause/resume or reload to retry; the session stays resumable.`,
      })
    })

    const fileId = uppy.addFile({
      name: file.name,
      type: file.type || 'video/mp4',
      data: file,
    })
    // Seeding s3Multipart routes Uppy through its resume path (listParts →
    // skip landed parts → upload the rest), for fresh and resumed uploads alike
    uppy.setFileState(fileId, {
      s3Multipart: { key, uploadId },
    } as unknown as Partial<UppyFile<Meta, Record<string, never>>>)

    this.uppy = uppy
    this.uppyFileId = fileId
    this.patch({
      phase: 'uploading',
      isPaused: false,
      errorMessage: null,
      bytesUploaded: 0,
      bytesTotal: file.size,
    })
    this.startPolling()
    uppy.upload().catch(() => {
      // Terminal errors surface via the upload-error listener
    })
  }

  private teardownUppy(): void {
    this.uppy?.destroy()
    this.uppy = null
    this.uppyFileId = null
  }

  private sizeOfPart(partNumber: number): number {
    const session = this.session
    if (!session) return 0
    const start = (partNumber - 1) * session.partSize
    return Math.min(session.declaredSizeBytes, start + session.partSize) - start
  }

  /** Part URLs are presigned in batches and cached until near expiry */
  private async getPartUrl(partNumber: number): Promise<string> {
    const cached = this.urlCache.get(partNumber)
    if (cached && Date.now() - cached.fetchedAt < PART_URL_TTL_MS) {
      return cached.url
    }

    const session = this.session
    if (!session) throw new Error('No upload session')

    const batchIndex = Math.floor((partNumber - 1) / PART_URL_BATCH)
    let pending = this.urlBatchPromises.get(batchIndex)
    if (!pending) {
      const first = batchIndex * PART_URL_BATCH + 1
      const last = Math.min(first + PART_URL_BATCH - 1, session.partCount)
      const numbers = Array.from({ length: last - first + 1 }, (_, i) => first + i)
      pending = fetchVodUploadPartUrls(this.assetId, session.uploadId, numbers)
        .then((response) => {
          const now = Date.now()
          for (const part of response.parts) {
            this.urlCache.set(part.part_number, { url: part.url, fetchedAt: now })
          }
        })
        .finally(() => {
          this.urlBatchPromises.delete(batchIndex)
        })
      this.urlBatchPromises.set(batchIndex, pending)
    }
    await pending

    const url = this.urlCache.get(partNumber)
    if (!url) throw new Error(`No presigned URL for part ${partNumber}`)
    return url.url
  }

  // ----------------------------------------------------------------
  // Finalize outcomes + the poll loop
  // ----------------------------------------------------------------

  private handleCompleteResult(uploadId: string, result: VodUploadCompleteResult): void {
    // Ignore stale outcomes from a session that was cancelled/replaced while
    // the request was in flight — they must not touch the successor's state
    if (this.destroyed || this.session?.uploadId !== uploadId) return
    switch (result.status) {
      case 'completed':
        this.stopPolling()
        this.clearStoredSession(uploadId)
        this.releaseTabLock()
        this.patch({
          phase: 'completed',
          session: result.session,
          warnings: result.warnings,
          errorMessage: null,
        })
        this.onCompleted()
        break
      case 'failed':
        this.stopPolling()
        this.clearStoredSession(uploadId)
        this.releaseTabLock()
        this.patch({ phase: 'failed', session: result.session, errorMessage: null })
        break
      case 'transient':
        // Session is back to UPLOADING with the object assembled; the poll
        // loop sees assembled: true and re-POSTs complete
        this.patch({
          phase: 'validating',
          session: result.session ?? null,
          errorMessage: result.detail ?? null,
        })
        this.startPolling()
        break
      case 'in_progress':
        if (result.session?.state === 'ABORTED') {
          this.stopPolling()
          this.clearStoredSession(uploadId)
          this.releaseTabLock()
          this.patch({ phase: 'idle', session: result.session })
          break
        }
        this.patch({ phase: 'validating', session: result.session ?? null })
        this.startPolling()
        break
    }
  }

  private async autoComplete(): Promise<void> {
    const session = this.session
    if (!session || this.completing) return
    this.completing = true
    try {
      const result = await completeVodUpload(this.assetId, session.uploadId)
      this.handleCompleteResult(session.uploadId, result)
    } catch (err) {
      if (!this.destroyed && this.session?.uploadId === session.uploadId) {
        this.patch({ errorMessage: (err as Error).message })
      }
    } finally {
      this.completing = false
    }
  }

  private startPolling(): void {
    if (this.pollTimer || this.destroyed) return
    this.pollTimer = setInterval(() => void this.pollTick(), POLL_INTERVAL_MS)
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  private async pollTick(): Promise<void> {
    const session = this.session
    if (!session || this.destroyed) return
    const uploadId = session.uploadId

    let data
    try {
      data = await pollVodUpload(this.assetId, uploadId)
    } catch {
      return // transient poll failure — next tick retries
    }
    // A cancel/start-over may have replaced the session mid-request; a stale
    // response must not steer the successor
    if (this.destroyed || this.session?.uploadId !== uploadId) return

    this.patch({ session: data.session })

    switch (data.session.state) {
      case 'COMPLETED':
        this.handleCompleteResult(uploadId, {
          status: 'completed',
          session: data.session,
          warnings: data.session.warnings,
          asset: data.asset,
        })
        break
      case 'FAILED':
        this.handleCompleteResult(uploadId, { status: 'failed', session: data.session })
        break
      case 'ABORTED':
        this.stopPolling()
        this.clearStoredSession(uploadId)
        this.releaseTabLock()
        this.patch({ phase: 'idle' })
        break
      case 'VALIDATING':
        if (isStaleValidating(data.session)) void this.autoComplete()
        break
      case 'UPLOADING':
        if (data.assembled) {
          // A transient finalize failure left an assembled master — heal it
          void this.autoComplete()
        } else if (
          this.phase === 'validating' &&
          (data.uploadedParts?.length ?? 0) >= (this.session?.partCount ?? Infinity)
        ) {
          // Parts all landed but the finalize request itself was lost — an
          // empty-body complete lets the server rebuild the manifest (ListParts)
          void this.autoComplete()
        } else if (this.uppy) {
          this.uploadedParts = data.uploadedParts ?? []
        } else {
          // No local uploader and parts still pending (e.g. a finalize that
          // lost its manifest) — the editor must re-pick the file to resume
          this.uploadedParts = data.uploadedParts ?? []
          const heldElsewhere = await this.isLockHeldElsewhere()
          if (this.destroyed || this.session?.uploadId !== uploadId) return
          if (heldElsewhere) {
            if (this.phase !== 'active-elsewhere') {
              this.patch({ phase: 'active-elsewhere' })
            }
          } else {
            this.stopPolling()
            this.patch({ phase: 'resume-prompt' })
          }
        }
        break
    }
  }

  /**
   * Remove the stored resume handle. When an uploadId is given, only remove
   * it if it still owns the key — a successor session's handle must survive
   * a stale predecessor's cleanup.
   */
  private clearStoredSession(uploadId?: string): void {
    const key = storageKey(this.assetId)
    if (uploadId && localStorage.getItem(key) !== uploadId) return
    localStorage.removeItem(key)
  }
}

export interface UseVodUploadResult extends VodUploadUiState {
  selectFile: (file: File) => void
  pauseResume: () => void
  cancel: () => Promise<void>
  startOver: () => Promise<void>
  reset: () => void
}

export const useVodUpload = (
  assetId: string,
  onCompleted: () => void,
  expectedMasterVersion: number
): UseVodUploadResult => {
  const [state, setState] = useState<VodUploadUiState>(INITIAL_STATE)
  const controllerRef = useRef<VodUploadController | null>(null)

  const onCompletedRef = useRef(onCompleted)
  const expectedMasterVersionRef = useRef(expectedMasterVersion)
  useEffect(() => {
    onCompletedRef.current = onCompleted
    expectedMasterVersionRef.current = expectedMasterVersion
  })

  // The current phase, readable from stable callbacks without re-binding them
  const phaseRef = useRef<VodUploadPhase>(state.phase)
  phaseRef.current = state.phase

  useEffect(() => {
    setState(INITIAL_STATE)
    const controller = new VodUploadController(
      assetId,
      (patch) => setState((prev) => ({ ...prev, ...patch })),
      () => onCompletedRef.current(),
      () => expectedMasterVersionRef.current
    )
    controllerRef.current = controller
    void controller.init()
    return () => {
      controllerRef.current = null
      controller.destroy()
    }
  }, [assetId])

  const selectFile = useCallback((file: File) => {
    void controllerRef.current?.selectFile(file, phaseRef.current)
  }, [])
  const pauseResume = useCallback(() => controllerRef.current?.pauseResume(), [])
  const cancel = useCallback(async () => {
    await controllerRef.current?.cancel()
  }, [])
  const startOver = useCallback(async () => controllerRef.current?.startOver(), [])
  const reset = useCallback(() => controllerRef.current?.reset(), [])

  return { ...state, selectFile, pauseResume, cancel, startOver, reset }
}
